import React, { useCallback } from "react";
import ExpertRecommendMessage from "../ExpertRecommendMessage";
import UnifiedContentRenderer from "./core/UnifiedContentRenderer";
import MsgToHumanContent from "./MsgToHumanContent";
import DraftMessageContent from "./DraftMessageContent";
import HumanMessageContent from "./HumanMessageContent";
import ExpertMessageContent from "./ExpertMessageContent";
import FinalDocContent from "./FinalDocContent";
import DefaultMessageContent from "./DefaultMessageContent";
import ExpertMessage from "../ExpertMessage";
import CollapsibleWrapper from "../CollapsibleWrapper";

const NOOP_TOGGLE = () => {};

const MessageRenderer = ({
  msg,
  index,
  options,
  renderCopyButton,
  shouldCollapseByValue,
  styles,
  convertExpertCommandsToDisplayNames,
  adoptedExpertOpinions,
  handleOpinionAdoption,
  selectedDiffLines,
  handleDiffLineClick,
  diffComments,
  handleCommentChange,
  handleExpertCommandSelect,
  websocket,
  preprocessContent,
  expertDialogContent,
  experts,
  handleExpertSelect,
  showNotification,
  inputRef,
  setHumanInput,
}) => {
  const { initialExpanded = false, onToggle = NOOP_TOGGLE } = options || {};
  const messageId = msg.id || `msg-${index}`;

  const handleA2UIAction = useCallback((actionObj) => {
    console.log("A2UI Action (方案B):", actionObj, messageId);

    const { name: actionName, context, displayText, autoSend } = actionObj || {};

    if (actionName && actionName.startsWith("skip_")) {
      console.log("A2UI: 跳过专家咨询 -", actionName);
    } else if (actionName && actionName.startsWith("call_expert")) {
      const expertsList = expertDialogContent?.experts || experts || [];
      console.log("A2UI: 检测到专家调用", displayText, "当前专家列表数量:", expertsList.length);

      // 归一化：去掉所有空白（含中文全角空格）、噪声词，统一小写。
      // - 空白：LLM 在中英混排专家名中常常额外加空格（"User Journey Expert"
      //   vs DB "UserJourneyExpert"，或 "用户旅程 Expert" vs "用户旅程Expert"），
      //   双向去空白后可对齐。
      // - 噪声词：LLM 偶尔会把按钮文字改成"推荐XX专家""咨询XX顾问"等。
      const normalize = (s) =>
        (s || "")
          .replace(/[\s\u3000]+/g, "")
          .replace(/专家|顾问|咨询|调用|推荐/g, "")
          .toLowerCase();

      const target = normalize(displayText);
      let matchedExpert = null;

      if (target) {
        // Pass 1：归一化后精确匹配 display_name / display_name_en / name / comd
        matchedExpert = expertsList.find((exp) =>
          [exp.display_name, exp.display_name_en, exp.name, exp.comd].some(
            (n) => n && normalize(n) === target
          )
        );

        // Pass 2：归一化后双向 includes（允许"调用XX""XX顾问"这类带前后缀的按钮文字），
        // 用最长匹配消除歧义；如出现并列最长则放弃，避免误匹配。
        if (!matchedExpert) {
          const candidates = [];
          for (const exp of expertsList) {
            let bestLen = 0;
            for (const name of [exp.display_name, exp.display_name_en, exp.name]) {
              const n = normalize(name);
              if (!n || n.length < 2) continue;
              if (target.includes(n) || n.includes(target)) {
                if (n.length > bestLen) bestLen = n.length;
              }
            }
            if (bestLen > 0) candidates.push({ exp, bestLen });
          }
          candidates.sort((a, b) => b.bestLen - a.bestLen);
          if (
            candidates.length === 1 ||
            (candidates.length > 1 && candidates[0].bestLen > candidates[1].bestLen)
          ) {
            matchedExpert = candidates[0].exp;
          } else if (candidates.length > 1) {
            console.warn(
              "A2UI: 专家名称歧义，多个候选并列最长：",
              candidates.map((c) => c.exp.display_name)
            );
          }
        }
      }

      if (matchedExpert) {
        console.log("A2UI: 匹配到专家", matchedExpert.display_name);
        handleExpertSelect(matchedExpert);
        return;
      }

      console.warn("A2UI: 未找到匹配的专家", displayText, "归一化:", target);
      showNotification(`未找到专家：${displayText || "未知"}，请在专家列表中手动选择`, "warning");
      return;
    }

    let textToFill = displayText;
    if (!textToFill && context) {
      const parts = Object.entries(context)
        .filter(([key, value]) => value != null && value !== "" && value !== "other")
        .map(([key, value]) => {
          if (Array.isArray(value)) return value.join("、");
          return String(value);
        });
      textToFill = parts.join("；") || actionName || "";
    }

    if (!textToFill) {
      console.warn("A2UI: 没有可填入的文本");
      return;
    }

    const textArea = inputRef.current || document.querySelector('textarea[name="humanInput"]');
    if (textArea) {
      textArea.value = textToFill;
      textArea.focus();
      setHumanInput(textToFill);
      const inputEvent = new Event("input", { bubbles: true });
      textArea.dispatchEvent(inputEvent);
      console.log("A2UI: 已填入输入框:", textToFill);

      if (autoSend) {
        setTimeout(() => {
          const form = textArea.closest("form");
          if (form) {
            const submitEvent = new Event("submit", { cancelable: true, bubbles: true });
            form.dispatchEvent(submitEvent);
            console.log("A2UI: 已触发表单提交");
          } else {
            const sendBtn =
              document.querySelector(".send-button") ||
              document.querySelector('button[type="submit"]');
            if (sendBtn) {
              sendBtn.click();
              console.log("A2UI: 已点击发送按钮");
            } else {
              console.warn("A2UI: 无法自动发送 - 未找到表单或发送按钮");
            }
          }
        }, 100);
      }
    } else {
      console.error("A2UI: 未找到输入框元素 (inputRef 和 querySelector 均失效)");
    }
  }, [
    expertDialogContent,
    experts,
    handleExpertSelect,
    inputRef,
    messageId,
    setHumanInput,
    showNotification,
  ]);

  const renderMermaidContent = (content) => {
    if (msg && (msg.type === "MsgtoHuman" || msg.type === "StreamingMsgtoHuman" || msg.role === "FinalDoc" || msg.role === "Expert")) {
      return (
        <ExpertMessage
          content={content}
          messageIndex={-1}
          adoptedOpinions={[]}
          onOpinionAdoption={() => {}}
          isError={false}
          isProcessing={false}
          styles={styles}
          onA2UIAction={handleA2UIAction}
          messageId={msg.id || `msg-${index}`}
          isStreaming={Boolean(msg.isStreaming)}
        />
      );
    }
    return (
      <UnifiedContentRenderer
        content={content}
        collapsible={true}
        isStreaming={Boolean(msg?.isStreaming || msg?.streaming)}
      />
    );
  };

  if (msg.type === "MsgtoHuman" || msg.type === "StreamingMsgtoHuman") {
    return (
      <MsgToHumanContent
        msg={msg}
        messageId={messageId}
        initialExpanded={initialExpanded}
        onToggle={onToggle}
        renderMermaidContent={renderMermaidContent}
        renderCopyButton={renderCopyButton}
        shouldCollapseByValue={shouldCollapseByValue}
        styles={styles}
      />
    );
  }

  if (msg.role === "Draft" || msg.type === "msg") {
    return (
      <DraftMessageContent
        msg={msg}
        index={index}
        messageId={messageId}
        initialExpanded={initialExpanded}
        onToggle={onToggle}
        shouldCollapseByValue={shouldCollapseByValue}
        renderCopyButton={renderCopyButton}
        styles={styles}
        selectedDiffLines={selectedDiffLines}
        handleDiffLineClick={handleDiffLineClick}
        diffComments={diffComments}
        handleCommentChange={handleCommentChange}
      />
    );
  }

  if (msg.type === "human" || msg.role === "Human" || msg.sendform === "Human" || msg.role === "Txt") {
    return (
      <HumanMessageContent
        msg={msg}
        messageId={messageId}
        initialExpanded={initialExpanded}
        onToggle={onToggle}
        shouldCollapseByValue={shouldCollapseByValue}
        renderCopyButton={renderCopyButton}
        convertExpertCommandsToDisplayNames={convertExpertCommandsToDisplayNames}
        styles={styles}
      />
    );
  }

  if (msg.role === "Expert") {
    return (
      <ExpertMessageContent
        msg={msg}
        index={index}
        messageId={messageId}
        initialExpanded={initialExpanded}
        onToggle={onToggle}
        shouldCollapseByValue={shouldCollapseByValue}
        renderCopyButton={renderCopyButton}
        styles={styles}
        adoptedExpertOpinions={adoptedExpertOpinions}
        handleOpinionAdoption={handleOpinionAdoption}
      />
    );
  }

  if (msg.type === "expert_recommendations") {
    return (
      <div className={`message-content-wrapper ${styles.messageContentWrapper || ""}`}>
        <CollapsibleWrapper
          messageId={messageId}
          collapsible={shouldCollapseByValue(msg.content)}
          initialExpanded={initialExpanded}
          onToggle={onToggle}
        >
          <ExpertRecommendMessage
            content={msg.content}
            onExpertSelect={handleExpertCommandSelect}
            websocket={websocket}
          />
        </CollapsibleWrapper>
      </div>
    );
  }

  if (msg.role === "Micro_scene") {
    return null;
  }

  if (msg.role === "FinalDoc") {
    return (
      <FinalDocContent
        msg={msg}
        messageId={messageId}
        initialExpanded={initialExpanded}
        onToggle={onToggle}
        preprocessContent={preprocessContent}
        renderMermaidContent={renderMermaidContent}
        renderCopyButton={renderCopyButton}
        styles={styles}
      />
    );
  }

  return (
    <DefaultMessageContent
      msg={msg}
      messageId={messageId}
      initialExpanded={initialExpanded}
      onToggle={onToggle}
      shouldCollapseByValue={shouldCollapseByValue}
      renderCopyButton={renderCopyButton}
      styles={styles}
    />
  );
};

export default React.memo(MessageRenderer);
